import type { LiveParsedSpot, LiveParsedSpots, LiveSpotList, SealedProduct, Store } from "@hobbyshopify/backend-client";
import { Rx, RxList } from "subtree.js/core";
import { type BoxDraft, type BoxPlan, EMPTY_BOX_DRAFT, planBox } from "../../core/primitives/box-builder";
import { type FormStatus, idleForm, idleLoad, type LoadStatus } from "../../core/primitives/load-status";

export class SellBoxState {
  readonly store = new Rx<Store | null>(null);
  readonly paymentsReady = new Rx(false);
  readonly draft = new Rx<BoxDraft>(EMPTY_BOX_DRAFT);
  /** Built-in leagues, the curated library and the shop's own saved lists, in that order. */
  readonly templates = new RxList<LiveSpotList>();
  readonly templatesStatus = new Rx<LoadStatus>(idleLoad);
  /** The name a pasted list is saved under. */
  readonly listName = new Rx("");
  readonly saveList = new Rx<FormStatus>(idleForm);
  /** In-stock sealed product the host can pick from. */
  readonly sealedProducts = new RxList<SealedProduct>();
  readonly sealedStatus = new Rx<LoadStatus>(idleLoad);
  readonly pasteOpen = new Rx(false);
  /** A pasted list is read by the server, on demand, and kept until it is replaced. */
  readonly pastedSpots = new RxList<LiveParsedSpot>();
  /** The server's last reading: its warnings and errors are said out loud rather than swallowed. */
  readonly parsed = new Rx<LiveParsedSpots | null>(null);
  readonly parse = new Rx<FormStatus>(idleForm);
  readonly cover = new Rx<FormStatus>(idleForm);
  readonly create = new Rx<FormStatus>(idleForm);
  /** Everything derived from the draft: the spot rows and the money. */
  readonly plan = new Rx<BoxPlan>(planBox(EMPTY_BOX_DRAFT, [], []));
}

export abstract class SellBoxActions {
  abstract setTitle(value: string): void;
  /** Picking a product names the stream and fills the money from what it cost and is worth; null returns to typing. */
  abstract pickSealedProduct(id: string | null): void;
  abstract setCost(value: string): void;
  abstract setMarketValue(value: string): void;
  abstract setMultiplier(value: number): void;
  abstract setSpotText(value: string): void;
  /** "Not earlier than": shown to buyers, and drawing earlier asks the host first. */
  abstract setBreakTime(value: string): void;
  /** A league list replaces a pasted one rather than sitting behind it looking selected. */
  abstract pickTemplate(key: string): void;
  abstract togglePaste(): void;
  abstract readSpots(): Promise<void>;
  /** A CSV, TSV or plain text file goes through the same reader as a paste. */
  abstract uploadList(file: File): Promise<void>;
  abstract setListName(value: string): void;
  /** Keeps the pasted list for next time, under `listName`. */
  abstract saveList(): Promise<void>;
  /** Removes one of the shop's own lists. */
  abstract removeList(spotListId: string): Promise<void>;
  abstract uploadCover(file: File): Promise<void>;
  abstract removeCover(): void;
  abstract create(): Promise<void>;
  /** Back to the queue with nothing put on sale, as iOS's Cancel: a phone has no tab bar under this page. */
  abstract cancel(): void;
}
